import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useStudioVoicePreviewContext } from '../context/StudioVoicePreviewContext';
import {
	PickerCloseButton,
	PickerDoneButton,
	PickerHeader,
	PickerModal,
	PickerOption,
	PickerOptions,
	PickerOverlay,
	PickerTitle,
} from '../studio.styles';

const PERSONALITY_KIND = 'personality';

const resolvePickerOptions = (kind, quizOptions) => {
	const apiKind = kind === PERSONALITY_KIND ? 'personality' : kind;
	const fromApi = quizOptions?.[apiKind];

	if (!fromApi?.length) {
		return [];
	}

	return fromApi.map((option) => ({
		value: option.value,
		label: option.label,
		previewAudioFileId: option.previewAudioFileId,
	}));
};

const StudioPickerModal = ({
	kind,
	value,
	onClose,
	onSelect,
	labels,
	quizOptions,
}) => {
	const { play, stop, prefetch } = useStudioVoicePreviewContext();
	const options = useMemo(
		() => resolvePickerOptions(kind, quizOptions),
		[kind, quizOptions],
	);
	const isKinks = kind === 'kinks';
	const isVoice = kind === 'voice';
	const selectedKinks = Array.isArray(value) ? value : [];
	const [draftValue, setDraftValue] = useState(value);

	useEffect(() => {
		setDraftValue(value);
	}, [value, kind]);

	useEffect(() => {
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = previousOverflow;
			stop();
		};
	}, [stop]);

	const handleKinkToggle = useCallback(
		(optionValue) => {
			if (selectedKinks.includes(optionValue)) {
				onSelect(selectedKinks.filter((item) => item !== optionValue));
				return;
			}

			if (selectedKinks.length >= 2) {
				return;
			}

			onSelect([...selectedKinks, optionValue]);
		},
		[onSelect, selectedKinks],
	);

	const handleOptionSelect = useCallback(
		(option) => {
			if (isVoice) {
				setDraftValue(option.value);

				if (option.previewAudioFileId) {
					play(option.previewAudioFileId);
				}

				return;
			}

			if (isKinks) {
				handleKinkToggle(option.value);
				return;
			}

			onSelect(option.value);
		},
		[handleKinkToggle, isKinks, isVoice, onSelect, play],
	);

	const handleDone = useCallback(() => {
		if (isVoice) {
			onSelect(draftValue);
		}

		onClose();
	}, [draftValue, isVoice, onClose, onSelect]);

	const handleOptionPointerDown = useCallback(
		(fileId) => {
			if (isVoice && fileId) {
				prefetch(fileId);
			}
		},
		[isVoice, prefetch],
	);

	return createPortal(
		<PickerOverlay onClick={onClose} role="presentation">
			<PickerModal
				onClick={(event) => event.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby="studio-picker-title"
			>
				<PickerHeader>
					<PickerTitle id="studio-picker-title">{labels[kind]}</PickerTitle>
					<PickerCloseButton type="button" aria-label={labels.back} onClick={onClose}>
						<svg viewBox="0 0 24 24" aria-hidden="true">
							<path d="m18 6-12 12M6 6l12 12" />
						</svg>
					</PickerCloseButton>
				</PickerHeader>
				<PickerOptions>
					{options.map((option) => {
						const active = isKinks
							? selectedKinks.includes(option.value)
							: isVoice
								? draftValue === option.value
								: value === option.value;

						return (
							<PickerOption
								key={option.value}
								type="button"
								$active={active}
								aria-pressed={active}
								onPointerDown={() =>
									handleOptionPointerDown(option.previewAudioFileId)
								}
								onClick={() => handleOptionSelect(option)}
							>
								{option.label}
							</PickerOption>
						);
					})}
				</PickerOptions>
				<PickerDoneButton type="button" onClick={handleDone}>
					{labels.done}
				</PickerDoneButton>
			</PickerModal>
		</PickerOverlay>,
		document.body,
	);
};

export default StudioPickerModal;
